import { Navigate, useLocation } from 'react-router-dom';
import { TENANT_NAV } from '../../lib/nav-config';
import { ehRotaDeResolucao } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { usePermissao } from '../../hooks/use-permissao';
import { leituraDaTela } from '../../lib/telas-acesso';
import { AcessoSuspensoSemPermissao } from '../AcessoSuspensoSemPermissao';
import { AppShell } from './AppShell';

/**
 * TenantLayout — shell pra área do tenant logado. O brand é resolvido
 * pelo AppShell via useBrandingStore (segue o `app_nome` da configuração
 * de sistema).
 *
 * [fix cold-start] Defense-in-depth: bloqueia acesso a qualquer rota
 * tenant sem sessão válida. O gate de hidratação do `App` (auth.store
 * .hidratado) já garante que esse check roda APÓS o persist aplicar o
 * snapshot — então não causa pisca (autenticado/usuario já têm o valor
 * canônico no primeiro render). Cobre o caso de uma rota tenant ser
 * acessada via deep-link cru sem token (cold start em /comercial/inbox
 * com localStorage limpo): redireciona pra /login sem montar o shell.
 *
 * O backend protege todas as rotas com JwtAuthGuard — este check é
 * apenas pra evitar montar a UI gated por nada.
 */
export function TenantLayout(): JSX.Element {
  const autenticado = useAuthStore((s) => s.autenticado);
  const usuario = useAuthStore((s) => s.usuario);
  const trialExpirado = useAuthStore((s) => s.trial_expirado);
  const { pode } = usePermissao();
  const { pathname } = useLocation();

  if (!autenticado || !usuario) {
    return <Navigate to="/login" replace />;
  }

  // [Bug#357] Tenant com trial vencido só alcança as rotas de resolução
  // (assinatura / chamados) — o backend já barra o resto via JwtAuthGuard.
  // Barrar AQUI, antes de montar, elimina o "flash": sem isto a página do módulo
  // montava, disparava request, tomava 403 e só então o interceptor fazia o
  // redirect (reload duro). O padrão é a assinatura — é lá que ele resolve.
  //
  // Mesmo aproveitando o mesmo gate de hidratação do `autenticado` acima, isto é
  // só uma OTIMIZAÇÃO de UX: a flag é um cache. Se estiver defasada, o
  // interceptor (fonte da verdade: o 403 do backend) ainda cobre.
  //
  // [AB#907] Mandar pra assinatura SÓ ajuda quem tem `config.assinatura:ler` — pra
  // quem não tem (hoje, de fábrica, todo mundo exceto Administrador), a tela de
  // assinatura é só outro 403 (`AcessoNegado`) na frente, um beco atrás do outro.
  // Sem a permissão: fica na própria navegação, com a tela de aviso (sem redirect,
  // sem loop) — ver `AcessoSuspensoSemPermissao`.
  if (trialExpirado && !ehRotaDeResolucao(pathname)) {
    if (!pode(leituraDaTela('mc-ass'))) {
      return <AcessoSuspensoSemPermissao />;
    }
    return <Navigate to="/minha-conta/assinatura#modulos" replace />;
  }

  return <AppShell nav={TENANT_NAV} />;
}
